import React, { useState, useEffect, useCallback } from 'react';

import { useSelector } from 'react-redux';
import { Plus, Clock, Eye, TrendingUp, Users } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import api from '../../services/api';
import { fixMediaUrl } from '../../utils/mediaHelpers';

import StoryBar from '../../components/features/stories/StoryBar';
import StoryCreator from '../../components/features/stories/StoryCreator';
import StoryViewer from '../../components/features/stories/StoryViewer';
import './StoriesPage.css';


const StoriesPage = () => {
  const { isDark } = useTheme();

  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState('all'); // all, my, popular

  const [stories, setStories] = useState([]);
  const [myStories, setMyStories] = useState([]);
  const [showCreator, setShowCreator] = useState(false);
  const [showViewer, setShowViewer] = useState(false);
  const [selectedStories, setSelectedStories] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [stats, setStats] = useState({
    totalViews: 0,
    totalStories: 0,
    avgViews: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStories();
    if (user?.id) {
      fetchMyStories();
    }
  }, [user?.id, activeTab]);


  const fetchStories = async () => {
    try {
      setLoading(true);
      const response = await api.get('/stories');
      if (response.data.success) {
        let storiesData = response.data.data;
        
        // Filtrer selon l'onglet actif
        if (activeTab === 'popular') {
          storiesData = storiesData.sort((a, b) => b.views_count - a.views_count);
        }
        
        setStories(storiesData);
      }
    } catch (error) {
      console.error('Error fetching stories:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyStories = async () => {
    try {
      const response = await api.get(`/stories?user_id=${user.id}`);
      if (response.data.success) {
        setMyStories(response.data.data);
        
        // Calculer les statistiques
        const totalViews = response.data.data.reduce((sum, s) => sum + s.views_count, 0);
        setStats({
          totalViews,
          totalStories: response.data.data.length,
          avgViews: response.data.data.length > 0 ? Math.round(totalViews / response.data.data.length) : 0,
        });
      }
    } catch (error) {
      console.error('Error fetching my stories:', error);
    }
  };

  const handleStoryClick = useCallback((story, allStories) => {
    setSelectedStories(allStories);
    setSelectedIndex(allStories.findIndex(s => s.id === story.id));
    setShowViewer(true);
  }, []);


  const handleDeleteStory = useCallback(async (storyId) => {
    if (!window.confirm('Supprimer cette story ?')) return;
    
    try {
      await api.delete(`/stories/${storyId}`);
      fetchMyStories();
      fetchStories();
    } catch (error) {
      console.error('Error deleting story:', error);
    }
  }, []);


  const formatTimeLeft = (expiresAt) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const diff = expires - now;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const renderStoryGrid = (storiesList, isMyStories = false) => {
    if (storiesList.length === 0) {
      return (
        <div className="empty-stories">
          <div className="empty-icon">📷</div>
          <p>Aucune story disponible</p>
          {isMyStories && (
            <button 
              className="btn-create-first"
              onClick={() => setShowCreator(true)}
            >
              Créer ma première story
            </button>
          )}
        </div>
      );
    }

    return (
      <div className="stories-grid">
        {storiesList.map((story) => (
          <div 
            key={story.id} 
            className="story-card"
            onClick={() => handleStoryClick(story, storiesList)}
          >
            <div className="story-thumbnail">
              {story.media_type === 'photo' ? (
                <img src={fixMediaUrl(story.media_file)} alt="Story" />
              ) : (
                <video src={fixMediaUrl(story.media_file)} />
              )}

              <div className="story-overlay">
                <span className="story-type">
                  {story.media_type === 'photo' ? '📷' : '🎥'}
                </span>
                <span className="story-duration">
                  <Clock size={14} />
                  {formatTimeLeft(story.expires_at)}
                </span>
              </div>
            </div>
            
            <div className="story-info">
              {!isMyStories && (
                <div className="story-author">
                  <img 
                    src={fixMediaUrl(story.user?.profile?.photo_url || story.user?.profile?.profile_picture) || '/default-avatar.svg'} 
                    alt={story.user?.first_name}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = '/default-avatar.svg';
                    }}

                  />
                  <span>{story.user?.first_name}</span>
                </div>
              )}



              
              <div className="story-stats">
                <span>
                  <Eye size={14} />
                  {story.views_count}
                </span>
                <span>
                  ❤️ {story.reactions_count}
                </span>
              </div>
              
              {story.caption && (
                <p className="story-caption-preview">{story.caption}</p>
              )}
            </div>
            
            {isMyStories && (
              <button 
                className="delete-story-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteStory(story.id);
                }}
              >
                ×
              </button>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`stories-page ${isDark ? 'dark' : ''}`}>

      {/* Header */}
      <div className="stories-header">
        <h1>Stories</h1>
        <button 
          className="btn-create-story"
          onClick={() => setShowCreator(true)}
        >
          <Plus size={20} />
          Créer une story
        </button>
      </div>

      {/* Story Bar (stories actives) */}
      <StoryBar currentUser={user} />

      {/* Tabs */}
      <div className="stories-tabs">
        <button 
          className={activeTab === 'all' ? 'active' : ''}
          onClick={() => setActiveTab('all')}
        >
          <Users size={18} />
          Toutes
        </button>
        <button 
          className={activeTab === 'my' ? 'active' : ''}
          onClick={() => setActiveTab('my')}
        >
          Mes stories
        </button>
        <button 
          className={activeTab === 'popular' ? 'active' : ''}
          onClick={() => setActiveTab('popular')}
        >
          <TrendingUp size={18} />
          Populaires
        </button>
      </div>

      {/* Content */}
      <div className="stories-content">
        {loading ? (
          <div className="stories-loading">
            <div className="spinner" />
            <p>Chargement des stories...</p>
          </div>
        ) : (
          <>
            {activeTab === 'my' && (
              <>
                {/* Stats pour mes stories */}
                <div className="my-stories-stats">
                  <div className="stat-card">
                    <span className="stat-value">{stats.totalStories}</span>
                    <span className="stat-label">Stories actives</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-value">{stats.totalViews}</span>
                    <span className="stat-label">Vues totales</span>
                  </div>
                  <div className="stat-card">
                    <span className="stat-value">{stats.avgViews}</span>
                    <span className="stat-label">Vues moyennes</span>
                  </div>
                </div>
                {renderStoryGrid(myStories, true)}
              </>
            )}
            
            {activeTab === 'all' && renderStoryGrid(stories)}
            {activeTab === 'popular' && renderStoryGrid(stories)}
          </>
        )}
      </div>

      {/* Modals */}
      {showCreator && (
        <StoryCreator 
          onClose={() => setShowCreator(false)}
          onStoryCreated={() => {
            fetchMyStories();
            fetchStories();
          }}
        />
      )}

      {showViewer && (
        <StoryViewer 
          stories={selectedStories}
          initialIndex={selectedIndex}
          onClose={() => {
            // Defer state updates to avoid React warning
            setTimeout(() => {
              setShowViewer(false);
              fetchStories();
            }, 0);
          }}
          currentUser={user}
        />
      )}

    </div>
  );
};

export default StoriesPage;
